import React, { useState, useEffect } from "react";
import apiClient from "../../../api/apiClient";
import Loading from "../../../components/Loading/Loading";

const ACTION_TYPES = [
    "COA_UPLOAD_SUCCESS",
    "COA_UPLOAD_FAILED",
    "TERPENE_PANEL_SUCCESS",
    "TERPENE_PANEL_FAILED",
    "SUPPLEMENTAL_PANEL_SUCCESS",
    "SUPPLEMENTAL_PANEL_FAILED",
    "FIELD_EDIT",
    "COA_DELETED",
    "COA_PUBLISHED",
    "COA_UNPUBLISHED",
    "PRODUCT_TRANSFERRED",
];

const BADGE_COLOR = {
    COA_UPLOAD_SUCCESS:        { background: "#d1fae5", color: "#065f46" },
    TERPENE_PANEL_SUCCESS:     { background: "#d1fae5", color: "#065f46" },
    SUPPLEMENTAL_PANEL_SUCCESS:{ background: "#d1fae5", color: "#065f46" },
    COA_UPLOAD_FAILED:         { background: "#fee2e2", color: "#991b1b" },
    TERPENE_PANEL_FAILED:      { background: "#fee2e2", color: "#991b1b" },
    SUPPLEMENTAL_PANEL_FAILED: { background: "#fee2e2", color: "#991b1b" },
    FIELD_EDIT:                { background: "#fef9c3", color: "#854d0e" },
    COA_DELETED:               { background: "#f3f4f6", color: "#374151" },
    COA_PUBLISHED:             { background: "#dbeafe", color: "#1e40af" },
    COA_UNPUBLISHED:           { background: "#ede9fe", color: "#5b21b6" },
    PRODUCT_TRANSFERRED:       { background: "#ffedd5", color: "#9a3412" },
};

const ActionBadge = ({ action }) => {
    const style = BADGE_COLOR[action] || { background: "#f3f4f6", color: "#374151" };
    return (
        <span style={{
            ...style,
            padding: "2px 8px",
            borderRadius: "9999px",
            fontSize: "11px",
            fontWeight: 600,
            whiteSpace: "nowrap",
        }}>
            {action}
        </span>
    );
};

const LIMIT = 25;

const COALedger = () => {
    const [entries, setEntries] = useState([]);
    const [total, setTotal]     = useState(0);
    const [page, setPage]       = useState(1);
    const [loading, setLoading] = useState(false);
    const [licenseFilter, setLicenseFilter] = useState("");
    const [actionFilter, setActionFilter]   = useState("");
    const [fromFilter, setFromFilter]       = useState("");
    const [toFilter, setToFilter]           = useState("");

    const fetchLedger = async (p = page) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: p, limit: LIMIT });
            if (licenseFilter) params.set("licenseNo", licenseFilter);
            if (actionFilter)  params.set("action", actionFilter);
            if (fromFilter)    params.set("dateFrom", fromFilter);
            if (toFilter)      params.set("dateTo", toFilter);
            const res = await apiClient.get(`/admin/coa-ledger?${params.toString()}`);
            setEntries(res.data.entries || []);
            setTotal(res.data.total || 0);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLedger(1); setPage(1); }, [licenseFilter, actionFilter, fromFilter, toFilter]);

    const totalPages = Math.ceil(total / LIMIT);

    const parseDetails = (raw) => {
        if (!raw) return null;
        try {
            return typeof raw === "string" ? JSON.parse(raw) : raw;
        } catch {
            return null;
        }
    };

    return (
        <div>
            {/* Filters */}
            <div className="admin-audit-filters">
                <input
                    type="text"
                    className="admin-audit-filter-input"
                    value={licenseFilter}
                    onChange={(e) => setLicenseFilter(e.target.value)}
                    placeholder="Filter by license"
                />
                <select
                    className="admin-audit-filter-input"
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                >
                    <option value="">All Actions</option>
                    {ACTION_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <input
                    type="date"
                    className="admin-audit-filter-input"
                    value={fromFilter}
                    onChange={(e) => setFromFilter(e.target.value)}
                />
                <input
                    type="date"
                    className="admin-audit-filter-input"
                    value={toFilter}
                    onChange={(e) => setToFilter(e.target.value)}
                />
                <span className="admin-search-count">{total} entries</span>
            </div>

            {loading ? (
                <div className="admin-loading-wrapper"><Loading /></div>
            ) : (
                <div className="admin-table-scroll">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th style={{ width: "13%" }}>Timestamp</th>
                                <th style={{ width: "19%" }}>Action</th>
                                <th style={{ width: "16%" }}>Product Name</th>
                                <th style={{ width: "9%" }}>DYC ID</th>
                                <th style={{ width: "12%" }}>User</th>
                                <th style={{ width: "12%" }}>Company</th>
                                <th style={{ width: "10%" }}>Location</th>
                                <th style={{ width: "9%" }}>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.length === 0 && (
                                <tr><td colSpan={8} className="admin-table-empty">No COA ledger entries.</td></tr>
                            )}
                            {entries.map((e) => {
                                const details = parseDetails(e.details);
                                const userName = (e.firstName || e.lastName)
                                    ? `${e.firstName || ""} ${e.lastName || ""}`.trim()
                                    : e.user_id;
                                return (
                                    <tr key={e.id}>
                                        <td className="admin-td-secondary" style={{ whiteSpace: "nowrap" }}>
                                            {new Date(e.created_at).toLocaleString()}
                                        </td>
                                        <td><ActionBadge action={e.action} /></td>
                                        <td className="admin-td-secondary" style={{ fontSize: "12px" }}>
                                            {e.product_name || details?.productName || "—"}
                                        </td>
                                        <td className="admin-td-secondary" style={{ fontSize: "11px", wordBreak: "break-all" }}>
                                            {e.dycId || "—"}
                                        </td>
                                        <td className="admin-td-secondary" style={{ fontSize: "11px", wordBreak: "break-all" }}>
                                            {userName || "—"}
                                        </td>
                                        <td className="admin-td-secondary" style={{ fontSize: "11px" }}>
                                            {e.company || "—"}
                                        </td>
                                        <td className="admin-td-secondary" style={{ fontSize: "11px" }}>
                                            {e.locationName || "—"}
                                        </td>
                                        <td className="admin-td-secondary" style={{ fontSize: "11px" }}>
                                            {details ? (
                                                <details>
                                                    <summary style={{ cursor: "pointer" }}>view</summary>
                                                    <pre style={{ fontSize: "10px", whiteSpace: "pre-wrap", maxWidth: "220px" }}>
                                                        {JSON.stringify(details, null, 2)}
                                                    </pre>
                                                </details>
                                            ) : "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {totalPages > 1 && (
                <div className="admin-pagination">
                    <span className="admin-pagination-info">
                        Page {page} of {totalPages} ({total} entries)
                    </span>
                    <div className="admin-pagination-btns">
                        <button
                            className="admin-action-btn primary"
                            disabled={page <= 1}
                            onClick={() => { const p = page - 1; setPage(p); fetchLedger(p); }}
                        >
                            Prev
                        </button>
                        <button
                            className="admin-action-btn primary"
                            disabled={page >= totalPages}
                            onClick={() => { const p = page + 1; setPage(p); fetchLedger(p); }}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default COALedger;
